import React, { useEffect, useState } from 'react';
import {
	Box,
	Flex,
	Heading,
	Table,
	Thead,
	Tbody,
	Tr,
	Th,
	Td,
	HStack,
	ButtonGroup,
	Button,
	Input,
	Modal,
	ModalOverlay,
	ModalContent,
	ModalHeader,
	ModalCloseButton,
	ModalBody,
	ModalFooter,
	FormControl,
	FormLabel,
	Textarea,
	Spinner,
	Text,
	Badge,
	useToast,
} from '@chakra-ui/react';
import { EditIcon, DeleteIcon, AddIcon } from '@chakra-ui/icons';
import {
	useReactTable,
	createColumnHelper,
	flexRender,
	getCoreRowModel,
} from '@tanstack/react-table';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import AdminNavbar from '../../component/adminNavbar';
import Alert from '../../component/alert';
import {
	getAllPriceGroups,
	createPriceGroup,
	deletePriceGroup,
} from '../../redux/actions/priceGroupActions';

const PriceGroups = ({ getAllPriceGroups, groups, loading, createPriceGroup, deletePriceGroup }) => {
	const navigate = useNavigate();
	const toast = useToast();
	const [isCreateOpen, setIsCreateOpen] = useState(false);
	const [isDeleteOpen, setIsDeleteOpen] = useState(false);
	const [selectedGroup, setSelectedGroup] = useState<any>(null);
	const [name, setName] = useState('');
	const [description, setDescription] = useState('');
	const [isSaving, setIsSaving] = useState(false);

	useEffect(() => {
		getAllPriceGroups();
	}, []);

	const openCreate = () => {
		setName('');
		setDescription('');
		setIsCreateOpen(true);
	};

	const handleCreate = async () => {
		if (!name.trim()) {
			toast({ title: 'Name is required.', status: 'warning', duration: 2000 });
			return;
		}
		setIsSaving(true);
		try {
			const created = await createPriceGroup(name.trim(), description, []);
			setIsCreateOpen(false);
			await getAllPriceGroups();
			const newId = created?.id || created?.insertId;
			if (newId) {
				navigate(`/admin/price-groups/${newId}`);
			}
		} finally {
			setIsSaving(false);
		}
	};

	const handleDelete = async () => {
		if (!selectedGroup) return;
		await deletePriceGroup(selectedGroup.id);
		setIsDeleteOpen(false);
		setSelectedGroup(null);
		getAllPriceGroups();
	};

	const columnHelper = createColumnHelper<any>();
	const columns = [
		columnHelper.accessor('id', { header: 'ID', cell: (info) => info.getValue() }),
		columnHelper.accessor('name', { header: 'Name', cell: (info) => info.getValue() }),
		columnHelper.accessor('description', { header: 'Description', cell: (info) => info.getValue() || '—' }),
		columnHelper.accessor('product_count', {
			header: 'Products',
			cell: (info) => <Badge colorScheme="blue">{info.getValue()}</Badge>,
		}),
		columnHelper.display({
			id: 'action',
			header: 'Actions',
			cell: ({ row }) => (
				<HStack spacing={2}>
					<ButtonGroup size="xs">
						<Button colorScheme="blue" onClick={() => navigate(`/admin/price-groups/${row.original.id}`)}>
							<EditIcon />
						</Button>
						<Button
							colorScheme="red"
							onClick={() => {
								setSelectedGroup(row.original);
								setIsDeleteOpen(true);
							}}
						>
							<DeleteIcon />
						</Button>
					</ButtonGroup>
				</HStack>
			),
		}),
	];

	const table = useReactTable({ columns, data: groups || [], getCoreRowModel: getCoreRowModel() });

	return (
		<>
			<Alert />
			<AdminNavbar />
			{!loading ? (
				<Box pt={{ base: 16, md: 6 }} px={{ base: 4, md: 6 }} pb={6} ml={{ base: 0, md: '250px' }} bg="gray.50" minH="100vh">
					<Heading mb={6} fontSize="2xl" fontWeight="bold" color="gray.700">
						Price Groups
					</Heading>

					<Box bg="white" p={4} borderRadius="lg" boxShadow="sm" mb={6}>
						<Flex justify="flex-start">
							<Button size="sm" colorScheme="green" leftIcon={<AddIcon />} onClick={openCreate} variant="outline">
								Create Price Group
							</Button>
						</Flex>
					</Box>

					<Box overflowX="auto" borderRadius="lg" bg="white" boxShadow="md">
						<Table variant="simple" colorScheme="gray" size="sm">
							<Thead bg="gray.100">
								{table.getHeaderGroups().map((hg) => (
									<Tr key={hg.id}>
										{hg.headers.map((h) => (
											<Th key={h.id}>{flexRender(h.column.columnDef.header, h.getContext())}</Th>
										))}
									</Tr>
								))}
							</Thead>
							<Tbody>
								{table.getRowModel().rows.length === 0 ? (
									<Tr>
										<Td colSpan={5}>
											<Text color="gray.400" textAlign="center" py={6} fontSize="sm">
												No price groups yet. Create one to get started.
											</Text>
										</Td>
									</Tr>
								) : (
									table.getRowModel().rows.map((row) => (
										<Tr
											key={row.id}
											_hover={{ bg: 'blue.50', cursor: 'pointer' }}
											transition="background 0.2s ease"
											onClick={() => navigate(`/admin/price-groups/${row.original.id}`)}
										>
											{row.getVisibleCells().map((cell) => (
												<Td
													key={cell.id}
													fontSize="sm"
													py={2}
													onClick={cell.column.id === 'action' ? (e) => e.stopPropagation() : undefined}
												>
													{flexRender(cell.column.columnDef.cell, cell.getContext())}
												</Td>
											))}
										</Tr>
									))
								)}
							</Tbody>
						</Table>
					</Box>

					{/* Create Modal — name/description only */}
					<Modal isOpen={isCreateOpen} onClose={() => setIsCreateOpen(false)} isCentered>
						<ModalOverlay />
						<ModalContent>
							<ModalHeader>Create Price Group</ModalHeader>
							<ModalCloseButton />
							<ModalBody>
								<FormControl mb={4} isRequired>
									<FormLabel>Name</FormLabel>
									<Input
										value={name}
										onChange={(e) => setName(e.target.value)}
										placeholder="e.g. Tier A"
										autoFocus
									/>
								</FormControl>
								<FormControl>
									<FormLabel>Description</FormLabel>
									<Textarea
										value={description}
										onChange={(e) => setDescription(e.target.value)}
										rows={3}
										placeholder="Optional notes about this pricing tier"
									/>
								</FormControl>
							</ModalBody>
							<ModalFooter>
								<Button variant="ghost" mr={3} onClick={() => setIsCreateOpen(false)}>
									Cancel
								</Button>
								<Button colorScheme="blue" onClick={handleCreate} isLoading={isSaving}>
									Create & Add Products
								</Button>
							</ModalFooter>
						</ModalContent>
					</Modal>

					{/* Delete Confirmation */}
					<Modal isOpen={isDeleteOpen} onClose={() => setIsDeleteOpen(false)} isCentered>
						<ModalOverlay />
						<ModalContent>
							<ModalHeader>Delete Price Group</ModalHeader>
							<ModalCloseButton />
							<ModalBody>
								Are you sure you want to delete <strong>{selectedGroup?.name}</strong>?
								Facilities assigned to this group will lose their group pricing.
							</ModalBody>
							<ModalFooter>
								<Button mr={3} onClick={() => setIsDeleteOpen(false)}>
									Cancel
								</Button>
								<Button colorScheme="red" onClick={handleDelete}>
									Delete
								</Button>
							</ModalFooter>
						</ModalContent>
					</Modal>
				</Box>
			) : (
				<Box ml={{ base: 0, md: '250px' }} p={10} display="flex" justifyContent="center">
					<Spinner size="xl" thickness="4px" speed="0.65s" color="blue.500" />
				</Box>
			)}
		</>
	);
};

const mapStateToProps = (state) => ({
	groups: state.priceGroups.groups,
	loading: state.priceGroups.loading,
});

const mapDispatchToProps = {
	getAllPriceGroups,
	createPriceGroup,
	deletePriceGroup,
};

export default connect(mapStateToProps, mapDispatchToProps)(PriceGroups);
